import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit3, Trash2, Layers, MessageSquare, Upload, Save, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import api from '../services/api';
import ImportModal from '../components/ImportModal';
import IconButton from '../components/IconButton';

const Faixas = () => {
  const { user } = useAuth();
  const [faixas, setFaixas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingFaixa, setEditingFaixa] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingFaixa, setDeletingFaixa] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    desc_faixa: '',
    valor_faixa: '',
    tp_conv_faixa: '',
    ativo_faixa: '',
    obs_faixa: '',
    id_serv_faixa: ''
  });
  const [obsModalItem, setObsModalItem] = useState(null);
  const [convencoes, setConvencoes] = useState([]);

  const formatBRL = (value) => {
    if (value === null || value === undefined || String(value).trim() === '') return '-';
    if (typeof value === 'number') {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    }
    try {
      const s = String(value).trim();
      const clean = s.replace(/[^0-9,.-]/g, '');
      const parsed = clean.includes(',') ? parseFloat(clean.replace(/\./g, '').replace(',', '.')) : parseFloat(clean);
      if (Number.isNaN(parsed)) return s;
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parsed);
    } catch (_) {
      return String(value);
    }
  };

  const formatCurrencyInput = (raw) => {
    try {
      const digits = String(raw || '').replace(/\D/g, '');
      if (!digits) return '';
      const number = parseInt(digits, 10) / 100;
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
    } catch (_) {
      return String(raw || '');
    }
  };

  useEffect(() => { loadFaixas(); }, []);
  useEffect(() => {
    document.title = 'Faixas - EstOX';
  }, []);
  useEffect(() => { loadConvencoes(); }, []);

  const loadFaixas = async () => {
    try {
      const response = await api.get('/faixas');
      setFaixas(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar faixas:', error);
      toast.error('Erro ao carregar faixas');
    } finally {
      setLoading(false);
    }
  };

  const loadConvencoes = async () => {
    try {
      const resp = await api.get('/convencao');
      setConvencoes(resp.data.data || []);
    } catch (error) {
      console.warn('Erro ao carregar convenções:', error?.response?.status || error);
    }
  };

  const filteredFaixas = faixas.filter(f => (
    String(f.desc_faixa ?? '').toLowerCase().includes(String(searchTerm).toLowerCase()) ||
    String(f.valor_faixa ?? '').toLowerCase().includes(String(searchTerm).toLowerCase()) ||
    String(f.id_serv_faixa ?? '').toLowerCase().includes(String(searchTerm).toLowerCase())
  ));

  const openModal = (faixa = null) => {
    setEditingFaixa(faixa);
    setFormData({
      desc_faixa: faixa?.desc_faixa || '',
      valor_faixa: formatCurrencyInput(faixa?.valor_faixa || ''),
      tp_conv_faixa: faixa?.tp_conv_faixa ?? '',
      ativo_faixa: faixa?.ativo_faixa ?? '',
      obs_faixa: faixa?.obs_faixa ?? '',
      id_serv_faixa: faixa?.id_serv_faixa ?? ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingFaixa(null);
    setFormData({ desc_faixa: '', valor_faixa: '', tp_conv_faixa: '', ativo_faixa: '', obs_faixa: '', id_serv_faixa: '' });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.desc_faixa.trim()) {
      toast.error('Descrição é obrigatória');
      return;
    }
    setIsSaving(true);
    try {
      if (editingFaixa) {
        await api.put(`/faixas/${editingFaixa.id}`, formData);
        toast.success('Faixa atualizada com sucesso!');
      } else {
        await api.post('/faixas', formData);
        toast.success('Faixa criada com sucesso!');
      }
      closeModal();
      loadFaixas();
    } catch (error) {
      console.error('Erro ao salvar faixa:', error);
      toast.error('Erro ao salvar faixa');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingFaixa) return;
    setIsDeleting(true);
    try {
      await api.delete(`/faixas/${deletingFaixa.id}`);
      toast.success('Faixa excluída com sucesso!');
      setShowDeleteModal(false);
      setDeletingFaixa(null);
      loadFaixas();
    } catch (error) {
      console.error('Erro ao excluir faixa:', error);
      toast.error('Erro ao excluir faixa');
    } finally {
      setIsDeleting(false);
    }
  };

  const importFields = [
    { key: 'id_serv_faixa', label: 'ID Supera' },
    { key: 'desc_faixa', label: 'Descrição', required: true },
    { key: 'valor_faixa', label: 'Valor' },
    { key: 'tp_conv_faixa', label: 'Tipo Conversão' },
    { key: 'ativo_faixa', label: 'Ativo' },
    { key: 'obs_faixa', label: 'Observações' }
  ];

  const handleImportData = async (data) => {
    setShowImportModal(false);
    const toastId = toast.loading('Importando faixas...');
    let successCount = 0;
    let errorCount = 0;

    for (const row of data) {
      try {
        // Resolve Tipo de Conversão
        let tpConv = row.tp_conv_faixa;
        if (tpConv && isNaN(tpConv)) {
          const conv = convencoes.find(c => 
            String(c.descricao_conv).toLowerCase().trim() === String(tpConv).toLowerCase().trim()
          );
          if (conv) tpConv = conv.id;
          else tpConv = null;
        }

        // Format Value
        let valor = row.valor_faixa;
        if (typeof valor === 'number') {
           valor = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(valor);
        }

        // Resolve Ativo
        let ativo = 1;
        if (row.ativo_faixa !== undefined && row.ativo_faixa !== null && row.ativo_faixa !== '') {
           const val = String(row.ativo_faixa).toLowerCase();
           if (val === '0' || val === 'false' || val === 'inativo' || val === 'não') ativo = 0;
           else ativo = 1;
        }

        const payload = {
          id_serv_faixa: row.id_serv_faixa,
          desc_faixa: row.desc_faixa,
          valor_faixa: valor,
          tp_conv_faixa: tpConv,
          ativo_faixa: ativo,
          obs_faixa: row.obs_faixa
        };

        // Clean empty values
        Object.keys(payload).forEach(key => {
            if (payload[key] === undefined || payload[key] === null || payload[key] === '') {
                delete payload[key];
            }
        });

        await api.post('/faixas', payload);
        successCount++;
      } catch (err) {
        console.error('Falha ao importar linha', row, err);
        errorCount++;
      }
    }

    toast.dismiss(toastId);
    if (errorCount === 0) {
      toast.success(`${successCount} faixas importadas com sucesso!`);
    } else {
      toast.success(`${successCount} importadas. ${errorCount} falhas.`);
    }
    loadFaixas();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Faixas</h1>
        <p className="text-gray-600">Gerencie faixas de conversão</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Layers className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Faixas</p>
              <p className="text-2xl font-bold text-gray-900">{faixas.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Search className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Filtradas</p>
              <p className="text-2xl font-bold text-gray-900">{filteredFaixas.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Plus className="h-8 w-8 text-purple-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Ações</p>
              <IconButton icon={Plus} onClick={() => openModal()} variant="primary" title="Nova Faixa" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Buscar faixas..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <IconButton icon={Upload} onClick={() => setShowImportModal(true)} variant="success" title="Importar" />
              <IconButton icon={Plus} onClick={() => openModal()} variant="primary" title="Nova Faixa" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID Supera</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descrição</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo Conversão</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ativo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Obs.</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredFaixas.map((faixa) => (
                <tr key={faixa.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">{faixa.id}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{faixa.id_serv_faixa || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{faixa.desc_faixa || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{formatBRL(faixa.valor_faixa)}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{
                    (faixa.tipo_conv_desc != null && faixa.tipo_conv_desc !== '')
                      ? faixa.tipo_conv_desc
                      : (faixa.tp_conv_faixa ?? '-')
                  }</td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {faixa.ativo_faixa === 1 ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        ATIVO
                      </span>
                    ) : faixa.ativo_faixa === 0 ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        INATIVO
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <IconButton
                      icon={MessageSquare}
                      onClick={() => setObsModalItem(faixa)}
                      variant="info"
                      size="sm"
                      title={faixa.obs_faixa != null && String(faixa.obs_faixa).trim() ? 'Ver observação' : 'Sem observação'}
                      disabled={!(faixa.obs_faixa != null && String(faixa.obs_faixa).trim())}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <IconButton icon={Edit3} onClick={() => openModal(faixa)} variant="warning" size="sm" title="Editar" />
                      <IconButton icon={Trash2} onClick={() => { setDeletingFaixa(faixa); setShowDeleteModal(true); }} variant="danger" size="sm" title="Excluir" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredFaixas.length === 0 && (
          <div className="text-center py-12">
            <Layers className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma faixa encontrada</h3>
            <p className="mt-1 text-sm text-gray-500">{searchTerm ? 'Tente ajustar sua busca.' : 'Comece criando uma nova faixa.'}</p>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{editingFaixa ? 'Editar Faixa' : 'Nova Faixa'}</h3>
              <form onSubmit={handleSave} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ID Supera</label>
                  <input type="text" maxLength="8" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={formData.id_serv_faixa} onChange={(e) => setFormData({ ...formData, id_serv_faixa: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrição *</label>
                  <input type="text" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={formData.desc_faixa} onChange={(e) => setFormData({ ...formData, desc_faixa: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={formData.valor_faixa}
                    onChange={(e) => setFormData({ ...formData, valor_faixa: formatCurrencyInput(e.target.value) })}
                    placeholder="R$ 0,00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Conversão</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={formData.tp_conv_faixa}
                    onChange={(e) => setFormData({ ...formData, tp_conv_faixa: e.target.value })}
                  >
                    <option value="">Selecione</option>
                    {convencoes.map(c => (
                      <option key={c.id} value={c.id}>{c.descricao_conv}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ativo</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={formData.ativo_faixa}
                    onChange={(e) => setFormData({ ...formData, ativo_faixa: e.target.value })}
                  >
                    <option value="">Selecione</option>
                    <option value="1">ATIVO</option>
                    <option value="0">INATIVO</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    value={formData.obs_faixa}
                    onChange={(e) => setFormData({ ...formData, obs_faixa: e.target.value })}
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <IconButton icon={X} onClick={closeModal} variant="secondary" title="Cancelar" />
                  <IconButton icon={Save} type="submit" disabled={isSaving} variant="primary" title={isSaving ? 'Salvando...' : (editingFaixa ? 'Atualizar' : 'Criar')} />
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

  {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-2">Confirmar Exclusão</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">Tem certeza que deseja excluir a faixa "{deletingFaixa?.desc_faixa}"? Esta ação não pode ser desfeita.</p>
              </div>
              <div className="flex justify-center space-x-3 px-4 py-3">
                <IconButton icon={X} onClick={() => { setShowDeleteModal(false); setDeletingFaixa(null); }} variant="secondary" title="Cancelar" />
                <IconButton icon={Trash2} onClick={handleDelete} disabled={isDeleting} variant="danger" title={isDeleting ? 'Excluindo...' : 'Excluir'} />
              </div>
            </div>
          </div>
      </div>
    )}

    {/* Modal de Observação */}
    {obsModalItem && (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={() => setObsModalItem(null)}>
        <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white" onClick={(e) => e.stopPropagation()}>
          <div className="mt-3">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Observação da Faixa #{obsModalItem.id}</h3>
            <div className="p-3 border border-gray-200 rounded bg-gray-50 whitespace-pre-wrap text-sm text-gray-800">
              {obsModalItem.obs_faixa != null && String(obsModalItem.obs_faixa).trim() ? String(obsModalItem.obs_faixa) : 'Sem observação.'}
            </div>
            <div className="mt-4 flex justify-end">
              <IconButton icon={X} onClick={() => setObsModalItem(null)} variant="secondary" title="Fechar" />
            </div>
          </div>
        </div>
      </div>
    )}
    
    <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImportData}
        fields={importFields}
        title="Importar Faixas"
      />
    </div>
  );
};

export default Faixas;
